import React from "react";
import { CATEGORIES, CITIES, categoryById } from "../lib/data";
import useCityAreas from "../lib/useCityAreas";
import { Button } from "./ui/button";

const SELLER_TYPES = [
  { value: "", label: "All Sellers" },
  { value: "owner", label: "Owner" },
  { value: "broker", label: "Agent" },
  { value: "professional", label: "Professional" },
];

const SORTS = [
  { value: "newest", label: "Newest" },
  { value: "price_low", label: "Price: Low to High" },
  { value: "price_high", label: "Price: High to Low" },
];

const inputCls = "w-full rounded-sm border border-border bg-white px-3 py-2 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary";

export default function FilterSidebar({ filters, onChange, onReset }) {
  const set = (patch) => onChange({ ...filters, ...patch });
  const cat = categoryById(filters.category);
  const subs = cat ? cat.subcategories : [];
  const { areas } = useCityAreas(filters.city);

  const toggleSub = (s) => {
    const cur = filters.subcategory || [];
    set({ subcategory: cur.includes(s) ? cur.filter((x) => x !== s) : [...cur, s] });
  };

  return (
    <div className="space-y-6" data-testid="filter-sidebar">
      <div className="flex items-center justify-between">
        <h2 className="font-display font-semibold text-ink">Filters</h2>
        <button onClick={onReset} className="text-xs text-accent font-medium hover:underline" data-testid="reset-filters-btn">Clear all</button>
      </div>

      <div>
        <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Category</label>
        <div className="mt-2 space-y-1.5">
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="radio" name="category" checked={!filters.category} onChange={() => set({ category: "", subcategory: [] })} className="accent-[#1B4F72]" /> All Categories
          </label>
          {CATEGORIES.map((c) => (
            <label key={c.id} className="flex items-center gap-2 text-sm cursor-pointer" data-testid={`filter-cat-${c.id}`}>
              <input type="radio" name="category" checked={filters.category === c.id} onChange={() => set({ category: c.id, subcategory: [] })} className="accent-[#1B4F72]" /> {c.label}
            </label>
          ))}
        </div>
      </div>

      {subs.length > 0 && (
        <div>
          <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Subcategory</label>
          <div className="mt-2 space-y-1.5">
            {subs.map((s) => (
              <label key={s} className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={(filters.subcategory || []).includes(s)} onChange={() => toggleSub(s)} className="accent-[#1B4F72] rounded" /> {s}
              </label>
            ))}
          </div>
        </div>
      )}

      <div>
        <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">City</label>
        <select value={filters.city || ""} onChange={(e) => set({ city: e.target.value, area: "" })} className={`mt-2 ${inputCls}`} data-testid="filter-city">
          <option value="">All Cities</option>
          {CITIES.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>

      {filters.city && (
        <div>
          <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Area</label>
          <select value={filters.area || ""} onChange={(e) => set({ area: e.target.value })} className={`mt-2 ${inputCls}`} data-testid="filter-area">
            <option value="">All Areas</option>
            {areas.map((a) => <option key={a} value={a}>{a}</option>)}
            <option value="__other__">Other…</option>
          </select>
          {filters.area === "__other__" && (
            <input value="" onChange={(e) => set({ area: e.target.value })} placeholder="Type your area" className={`mt-2 ${inputCls}`} />
          )}
        </div>
      )}

      <div>
        <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Price Range (₹)</label>
        <div className="mt-2 flex items-center gap-2">
          <input type="number" placeholder="Min" value={filters.minPrice || ""} onChange={(e) => set({ minPrice: e.target.value })} className={inputCls} data-testid="filter-min-price" />
          <span className="text-muted-foreground">-</span>
          <input type="number" placeholder="Max" value={filters.maxPrice || ""} onChange={(e) => set({ maxPrice: e.target.value })} className={inputCls} data-testid="filter-max-price" />
        </div>
      </div>

      <div>
        <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Seller Type</label>
        <select value={filters.sellerType || ""} onChange={(e) => set({ sellerType: e.target.value })} className={`mt-2 ${inputCls}`} data-testid="filter-seller-type">
          {SELLER_TYPES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
      </div>

      <div>
        <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Sort By</label>
        <select value={filters.sort || "newest"} onChange={(e) => set({ sort: e.target.value })} className={`mt-2 ${inputCls}`} data-testid="filter-sort">
          {SORTS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
      </div>
    </div>
  );
}
